// src/encounter/care-plan-tab/care-plan-labels.ts
// The Care plan tab's words and look: lane names in the demo's order, its legend words and icons, its lane hues as DS
// token classes, its type roles, the board's status labels and its 12 weeks.
import { Activity, ClipboardList, MessageSquare, Pill, Stethoscope, UserRound, type LucideIcon } from 'lucide-react';
import { cn, TEXT_ROLE_CLASSES } from '@scripsteam/scrips-signal-ds';
import type { CarePlanActivityView, CarePlanLane } from '../lib/care-plan-view';

/** The demo's lane names, in its order. */
export const CARE_PLAN_LANE_LABEL: Record<CarePlanLane, string> = {
  'physician-orders': 'Physician orders',
  nutrition: 'Nutrition',
  exercise: 'Exercise',
  coaching: 'Coaching',
  supplements: 'Supplements',
  physiotherapy: 'Physiotherapy',
};
export const PLAN_WEEKS = 12;

/** The board's word for a status. "AI draft" is a label only (Andrew 4 Nov): no engine call. */
export const ACTIVITY_STATUS_LABEL: Record<CarePlanActivityView['status'], string> = {
  'ai-draft': 'AI draft',
  draft: 'Draft',
  accepted: 'Accepted · unsigned',
  signed: 'Signed',
};

/** The demo's legend words per lane ("Order type"). */
export const CARE_PLAN_LANE_LEGEND: Record<CarePlanLane, string> = {
  'physician-orders': 'Physician orders & medications',
  nutrition: 'Nutrition',
  exercise: 'Exercise',
  coaching: 'Coaching',
  supplements: 'Supplements',
  physiotherapy: 'Physiotherapy',
};

/**
 * The demo's lane icons (its `ir` is the DS Icon: doctor, clipboard, activity, message, pill, patient), as the Lucide
 * glyphs the DS Icon draws for those names (icons are Lucide only in this app, PROD-1599).
 */
export const CARE_PLAN_LANE_ICON: Record<CarePlanLane, LucideIcon> = {
  'physician-orders': Stethoscope,
  nutrition: ClipboardList,
  exercise: Activity,
  coaching: MessageSquare,
  supplements: Pill,
  physiotherapy: UserRound,
};

/**
 * The demo's lane hues (uQ: orange, jade, purple, vermilion, sky, yellow) as the DS categorical tokens (--color-cat-*),
 * at the steps the demo uses (100 / 200 / 400 / 500 / 600 / 800). Literal class names, so Tailwind generates them.
 *   tile: the icon tile (100 fill, 600 ink)    rule: the 500 left rule (team card, lane stripe)
 *   bar: an accepted or signed bar (100 fill, 200 edge, 800 ink)    draft: an AI draft bar (400 edge, no fill)
 */
export const CARE_PLAN_LANE_LOOK: Record<CarePlanLane, { tile: string; ink: string; rule: string; bar: string; draft: string }> = {
  'physician-orders': {
    tile: 'bg-cat-orange-100 text-cat-orange-600',
    ink: 'text-cat-orange-600',
    rule: 'border-cat-orange-500',
    bar: 'bg-cat-orange-100 border-cat-orange-200 text-cat-orange-800',
    draft: 'border-cat-orange-400',
  },
  nutrition: {
    tile: 'bg-cat-jade-100 text-cat-jade-600',
    ink: 'text-cat-jade-600',
    rule: 'border-cat-jade-500',
    bar: 'bg-cat-jade-100 border-cat-jade-200 text-cat-jade-800',
    draft: 'border-cat-jade-400',
  },
  exercise: {
    tile: 'bg-cat-purple-100 text-cat-purple-600',
    ink: 'text-cat-purple-600',
    rule: 'border-cat-purple-500',
    bar: 'bg-cat-purple-100 border-cat-purple-200 text-cat-purple-800',
    draft: 'border-cat-purple-400',
  },
  coaching: {
    tile: 'bg-cat-vermilion-100 text-cat-vermilion-600',
    ink: 'text-cat-vermilion-600',
    rule: 'border-cat-vermilion-500',
    bar: 'bg-cat-vermilion-100 border-cat-vermilion-200 text-cat-vermilion-800',
    draft: 'border-cat-vermilion-400',
  },
  supplements: {
    tile: 'bg-cat-sky-100 text-cat-sky-600',
    ink: 'text-cat-sky-600',
    rule: 'border-cat-sky-500',
    bar: 'bg-cat-sky-100 border-cat-sky-200 text-cat-sky-800',
    draft: 'border-cat-sky-400',
  },
  physiotherapy: {
    tile: 'bg-cat-yellow-100 text-cat-yellow-600',
    ink: 'text-cat-yellow-600',
    rule: 'border-cat-yellow-500',
    bar: 'bg-cat-yellow-100 border-cat-yellow-200 text-cat-yellow-800',
    draft: 'border-cat-yellow-400',
  },
};

/**
 * The demo's type roles (its `L`) as DS roles: cardTitle → card-title, bodyStrong → body-emphasis, body → body,
 * meta → meta, label → 12 medium. The inks are the demo's own tokens (--ink-body / --ink-secondary / --ink-meta).
 */
export const CP_CARD_TITLE = cn(TEXT_ROLE_CLASSES['card-title'], 'm-0 text-ink-body');
export const CP_BODY_STRONG = cn(TEXT_ROLE_CLASSES['body-emphasis'], 'text-ink-body');
export const CP_BODY = cn(TEXT_ROLE_CLASSES.body, 'text-ink-body');
export const CP_META = cn(TEXT_ROLE_CLASSES.meta, 'text-ink-secondary');
export const CP_LABEL = 'text-xs font-medium';
/** The demo's link-style action (Review / Sign / Open / Show): accent ink, a tint on hover, 24 tall. */
export const CP_LINK_ACTION = cn(
  CP_LABEL,
  'h-6 shrink-0 rounded-sm bg-transparent px-2 text-accent-active-text hover:bg-accent-active-bg',
  'disabled:cursor-not-allowed disabled:text-ink-secondary disabled:hover:bg-transparent',
);
/** The demo's two white cards (team, Needs you now): a 1px line, the card fill, 20 padding. */
export const CP_WHITE_CARD = 'rounded-card border border-surface-line bg-surface-card p-5';

/** The weeks an item is drawn across, clamped to the board; `past` = it runs after W12 (said in words, never hidden). */
export function boardWeeks(weekFrom: number, weekTo: number): { from: number; to: number; past: boolean } {
  const from = Math.min(Math.max(1, weekFrom), PLAN_WEEKS);
  const to = Math.min(Math.max(from, weekTo), PLAN_WEEKS);
  return { from, to, past: weekTo > PLAN_WEEKS };
}

/** "W1–W12" (the demo's b_), plus our "(runs past W12)" / "after W12" so a clamped bar is never silent. */
export function weeksText(a: Pick<CarePlanActivityView, 'weekFrom' | 'weekTo'>): string {
  if (a.weekFrom > PLAN_WEEKS) return `after W${PLAN_WEEKS}`;
  const range = a.weekTo !== a.weekFrom ? `W${a.weekFrom}–W${a.weekTo}` : `W${a.weekFrom}`;
  return a.weekTo > PLAN_WEEKS ? `${range} (runs past W${PLAN_WEEKS})` : range;
}

/** A goal's track, in words (C5 track: metabolic | function | lifestyle | other). */
export const GOAL_TRACK_LABEL: Record<string, string> = {
  metabolic: 'Metabolic',
  function: 'Function',
  lifestyle: 'Lifestyle',
  other: 'Other',
};
